export const USDFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    roundingMode: undefined,
})

/**
 * Parse a localized number to a float.
 * @param {string} stringNumber - the localized number
 * @param {string} locale - [optional] the locale that the number is represented in. Omit this parameter to use the current locale.
 */
export function reverseUSDFormatter(stringNumber) {
    var thousandSeparator = Intl.NumberFormat('en-US')
        .format(11111)
        .replace(/\p{Number}/gu, '')
    var decimalSeparator = Intl.NumberFormat('en-US')
        .format(1.1)
        .replace(/\p{Number}/gu, '')

    return parseFloat(
        stringNumber
            .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
            .replace(new RegExp('\\' + decimalSeparator), '.')
    )
}

export const decoratedDelta = (delta) => {
    if (delta === '0') return ''
    const prefix = delta > 0 ? '▲' : '▼'
    return `${prefix} ${Math.abs(delta * 100).toFixed(1)}%`
}

export const abbreviatedDollar = (amount) => {
    if (amount < 1_000) return `$${amount}`
    else if (amount < 100_000) return `$${(amount / 1_000).toFixed(1)}K`
    else if (amount < 1_000_000) return `$${(amount / 1_000).toFixed(0)}K`
    return `$${(amount / 1_000_000).toFixed(2)}M`
}

// Use half even rounding, which is not supported by Intl.NumberFormat
// https://en.wikipedia.org/wiki/Rounding#Round_half_to_even
export const currencyFloat = (n) => {
    if (typeof n === 'undefined') return n
    const str = Number(n).toFixed(4)

    if (Number(str) < 0) return -1 * currencyFloat(-n)

    const numberOfDecimalDigits = 2
    const isEven = (d) => d % 2 === 0

    const decimalIndex = str.indexOf('.')
    if (decimalIndex < 0) return Number(n)

    const integerPortion = Number(str.slice(0, decimalIndex))

    let decimalPortion = str.slice(decimalIndex + 1)
    if (decimalPortion.length <= numberOfDecimalDigits) return Number(n)

    // Save the decimal portion we care about
    const newDecimalPortion = decimalPortion.slice(0, numberOfDecimalDigits)
    const completedNumber = Number(`${integerPortion}.${newDecimalPortion}`)

    const lastDigit = Number(decimalPortion[numberOfDecimalDigits])
    // If the last digit is below 5, there is no need to round up, just truncate
    if (lastDigit < 5) {
        return completedNumber
    }

    // Round up
    if (lastDigit > 5) {
        const decimalWithExtraDigit = decimalPortion.slice(0, numberOfDecimalDigits + 1)
        const number = Number(`${integerPortion}.${decimalWithExtraDigit}`)
        return Number(number.toFixed(numberOfDecimalDigits))
    }

    // Tie break time

    // Get the digit before lastDigit to determine if we round up when it is odd
    // or if we do nothing, when it is even
    const digitBeforeLastDigit = Number(decimalPortion[numberOfDecimalDigits - 1])
    if (isEven(digitBeforeLastDigit)) {
        return completedNumber
    }

    // number before is odd, round up
    const number = completedNumber + 0.01
    return Number(number.toFixed(numberOfDecimalDigits))
}

export const currencyFormat = (amount, alwaysShowDecimals = false, round = false) => {
    if (amount === undefined) {
        return 'TBD'
    }

    const str = USDFormatter.format(currencyFloat(amount))
    if (!alwaysShowDecimals && str.endsWith('.00')) {
        return str.substring(0, str.length - 3)
    }
    return str
}

export const currencyFormatReporting = (
    value,
    maximumFractionDigits = 2,
    minimumFractionDigits = null
) => {
    if (value == null) {
        return ''
    }

    const isNegative = value < 0
    value = Math.abs(value)

    // Determine minimum fraction digits if not provided
    if (minimumFractionDigits == null) {
        minimumFractionDigits = value % 1 !== 0 ? 2 : 0
    }

    // Format the value
    const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD', // You might want to make this configurable
        minimumFractionDigits: minimumFractionDigits,
        maximumFractionDigits: maximumFractionDigits,
    })

    let formatted = formatter.format(value)

    // Add negative sign if necessary
    if (isNegative) {
        formatted = `-${formatted}`
    }

    return formatted
}

export const shiftThirdDecimal = function (value) {
    const monetaryFilter = /^(\d+)?\.\d{3,}$/
    if (value && monetaryFilter.test(value)) {
        value = value.replace('.', '')
        value = value.slice(0, value.length - 2) + '.' + value.slice(value.length - 2, value.length)
    }
    return value
}

export const filterMonetaryInput = (value, setterCallback) => {
    const monetaryFilter = /^\d+(\.\d{0,2})?$/
    if (value && !monetaryFilter.test(value)) return

    const inputFilter = /[^0-9.]+/
    if (setterCallback !== undefined) {
        setterCallback(value.replace(inputFilter, ''))
    }
    return value.replace(inputFilter, '')
}

export const filterNumericInput = (value, setterCallback) => {
    const inputFilter = /[^0-9]/
    if (setterCallback !== undefined) {
        setterCallback(value.replace(inputFilter, ''))
    }
    return value.replace(inputFilter, '')
}

export const filterDecimalInput = (value, setterCallback) => {
    const inputFilter = /[^0-9.]/
    setterCallback(value.replace(inputFilter, ''))
}

export const generateRandomID = () => {
    const crypto = window.crypto || window.msCrypto
    const randomInt = crypto.getRandomValues(new Uint32Array(1))[0]
    return randomInt.toString(16)
}

export const formatFloat = (params) => {
    const { value, ndigits } = params
    if (value !== undefined && value !== Math.floor(value)) {
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: ndigits,
        })
        return formatter.format(value)
    }
    return String(Math.trunc(value))
}

export const formatDuration = (duration, allowDays = true) => {
    if (duration === null) return 'TBD'
    if (duration >= 60 * 24 && allowDays) {
        const days = Math.floor(duration / (60 * 24))
        const weeks = Math.floor(duration / (7 * 60 * 24))
        if (days === 1) return `${days} day`
        else if (weeks > 1 && days % 7)
            return days % 7 > 1
                ? `${weeks} weeks, ${days % 7} days`
                : `${weeks} weeks, ${days % 7} day`
        else if (weeks === 1 && days % 7)
            return days % 7 > 1
                ? `${weeks} week, ${days % 7} days`
                : `${weeks} week, ${days % 7} day`
        else if (weeks) return weeks > 1 ? `${weeks} weeks` : `${weeks} week`
        else if (days) return `${days} days`
    }
    else {
        const hours = Math.floor(duration / 60)
        const minutes = duration % 60
        if (hours < 1 && minutes > 0) return minutes > 1 ? `${duration} minutes` : '1 minute'
        else if (hours === 1 && minutes)
            return minutes > 1 ? `1 hour, ${minutes} minutes` : '1 hour, 1 minute'
        else if (hours === 1) return `${hours} hour`
        else if (minutes)
            return minutes > 1 ? `${hours} hours, ${minutes} minutes` : `${hours} hours, 1 minute`
        else return `${hours} hours`
    }
}

export const formatNumberForReportMetric = (number, fieldType, fallback) => {
    if (fieldType === 'currency') {
        if (number >= 1_000) {
            return number >= 1_000_000
                ? `$${(number / 1_000_000).toFixed(1)}M`
                : `$${(number / 1_000).toFixed(1)}K`
        }
        else {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'decimal',
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
            })
            return `$${formatter.format(number)}`
        }
    }
    else if (fieldType === 'integer') {
        if (number >= 1_000) {
            return number >= 1_000_000
                ? `${(number / 1_000_000).toFixed(1)}M`
                : `${(number / 1_000).toFixed(1)}K`
        }
        else {
            const formatter = new Intl.NumberFormat('en-US', {
                style: 'decimal',
                maximumFractionDigits: 2,
                minimumFractionDigits: 0,
            })
            return `${formatter.format(number)}`
        }
    }
    else if (fieldType === 'percent') {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'decimal',
            maximumFractionDigits: 2,
            minimumFractionDigits: 0,
        })
        return `${formatter.format(number)}%`
    }
    return fallback
}

export const shiftCurrencyLeft = (originalValue, valueToAppend) => {
    const formattedOriginal = currencyFormat(originalValue ?? 0, true, false)
        .replace('$', '')
        .replace('.', '')
        .replaceAll(',', '')

    const appendedValue = formattedOriginal + valueToAppend

    return Number(appendedValue.slice(0, -2) + '.' + appendedValue.slice(-2))
}

export const shiftCurrencyRight = (originalValue) => {
    const formattedOriginal = currencyFormat(originalValue ?? 0, true, false)
        .replace('$', '')
        .replace('.', '')
        .replaceAll(',', '')

    const withLastValueRemoved = formattedOriginal.slice(0, -1)

    return Number(withLastValueRemoved.slice(0, -2) + '.' + withLastValueRemoved.slice(-2))
}

export const currencyFormatNoRounding = (amount) => {
    const truncateFractionAndFormat = (parts, digits) => {
        return parts
            .map(({ type, value }) => {
                if (type !== 'fraction' || !value || value.length < digits) {
                    return value
                }

                let retVal = ''
                for (let idx = 0, counter = 0; idx < value.length && counter < digits; idx++) {
                    if (value[idx] !== '0') {
                        counter++
                    }
                    retVal += value[idx]
                }
                return retVal
            })
            .reduce((string, part) => string + part)
    }

    if (amount === null || amount === undefined) {
        return '$0.00'
    }

    return truncateFractionAndFormat(USDFormatter.formatToParts(amount), 2)
}
